import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import type {
  CampaignAudiencePreviewResponse,
  CampaignAudienceUploadDto,
  CampaignAudienceUploadPreviewResponse,
  CampaignDto,
  ConfigureCampaignAudienceUploadRequest,
  ContactGroupSummary,
} from "@multi-chat/shared";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { CampaignAudienceCurrentSources } from "./CampaignAudienceCurrentSources.js";
import { CampaignAudiencePreview } from "./CampaignAudiencePreview.js";
import { CampaignAudienceSourceTabs } from "./CampaignAudienceSourceTabs.js";
import { CampaignContactPickerPanel } from "./CampaignContactPickerDialog.js";
import { CampaignCsvPanel } from "./CampaignCsvPanel.js";
import { CampaignGroupPickerPanel } from "./CampaignGroupPickerDialog.js";
import { CampaignModalContent } from "./CampaignModalContent.js";
import {
  csvSourceApplyCta,
  hasUnappliedAudienceSourceChanges,
  idSourceApplyCta,
  sourceApplyI18nKey,
  type AudienceSourceApplyCta,
} from "./campaign-audience-source-state.js";
import {
  CAMPAIGN_PANE_MOTION_MS,
  campaignHasAudienceSources,
  campaignManageSourceTab,
  csvBlocksAudiencePreview,
  type CampaignManageSourceTab,
  type CampaignManageView,
} from "./campaigns-presentation.js";
import { mapCampaignError } from "./campaigns-errors.js";

export function CampaignManageRecipientsDialog({
  open,
  view,
  organizationId,
  campaign,
  groups,
  upload,
  uploadPreview,
  preview,
  error,
  canMutate,
  saving,
  uploading,
  uploadProgress,
  polling,
  onOpenChange,
  onViewChange,
  onSetContactIds,
  onSetGroupIds,
  onApplyCsv,
  onUploadCsv,
  onConfigureUpload,
  onRemoveUpload,
  onPreview,
  onConfirm,
}: {
  open: boolean;
  view: CampaignManageView;
  organizationId: number;
  campaign: CampaignDto;
  groups: readonly ContactGroupSummary[];
  upload: CampaignAudienceUploadDto | null;
  uploadPreview: CampaignAudienceUploadPreviewResponse | null;
  preview: CampaignAudiencePreviewResponse | null;
  error: unknown;
  canMutate: boolean;
  saving: boolean;
  uploading: boolean;
  uploadProgress: number | null;
  polling: boolean;
  onOpenChange: (open: boolean) => void;
  onViewChange: (view: CampaignManageView) => void;
  onSetContactIds: (contactIds: number[]) => Promise<void>;
  onSetGroupIds: (groupIds: number[]) => Promise<void>;
  onApplyCsv: (uploadId: string) => Promise<void>;
  onUploadCsv: (file: File, worksheet?: string) => Promise<void>;
  onConfigureUpload: (body: ConfigureCampaignAudienceUploadRequest) => Promise<void>;
  onRemoveUpload: () => Promise<void>;
  onPreview: () => Promise<void>;
  onConfirm: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const definition = campaign.audienceDefinition;
  const [contactDraftIds, setContactDraftIds] = useState<number[]>(() => [...definition.contactIds]);
  const [groupDraftIds, setGroupDraftIds] = useState<number[]>(() => [...definition.contactGroupIds]);
  const [appliedCsvUploadId, setAppliedCsvUploadId] = useState<string | null>(
    () => definition.upload?.uploadId ?? null,
  );

  useEffect(() => {
    if (!open) return;
    setContactDraftIds([...campaign.audienceDefinition.contactIds]);
    setGroupDraftIds([...campaign.audienceDefinition.contactGroupIds]);
    setAppliedCsvUploadId(campaign.audienceDefinition.upload?.uploadId ?? null);
  }, [open, campaign.id]);

  const selectedGroups = groups.filter((group) =>
    definition.contactGroupIds.includes(Number(group.id)),
  );
  const acknowledgedCsvFileName =
    appliedCsvUploadId != null && upload?.uploadId === appliedCsvUploadId
      ? upload.originalFileName
      : null;
  const hasSources = campaignHasAudienceSources({
    ...definition,
    upload: appliedCsvUploadId ? { uploadId: appliedCsvUploadId } : null,
  });
  const previewBlocked = csvBlocksAudiencePreview(upload?.status ?? null);
  const previewForCurrent =
    preview && preview.definitionRevision === campaign.audienceDefinitionRevision
      ? preview
      : null;
  const readyCount = previewForCurrent?.summary.readyRecipients ?? 0;
  const sourceTab = campaignManageSourceTab(view);
  const isReview = view === "review";
  const paneDuration = reduceMotion ? 0 : CAMPAIGN_PANE_MOTION_MS / 1000;
  const anySourceDirty = hasUnappliedAudienceSourceChanges({
    committedContactIds: definition.contactIds,
    draftContactIds: contactDraftIds,
    committedGroupIds: definition.contactGroupIds,
    draftGroupIds: groupDraftIds,
    appliedCsvUploadId,
    workingCsvUploadId: upload?.uploadId ?? null,
  });
  const contactCta = idSourceApplyCta(definition.contactIds, contactDraftIds);
  const groupCta = idSourceApplyCta(definition.contactGroupIds, groupDraftIds);
  const csvCta = csvSourceApplyCta({
    appliedUploadId: appliedCsvUploadId,
    workingUploadId: upload?.uploadId ?? null,
    workingStatus: upload?.status ?? null,
    validCount: uploadPreview?.summary?.validRowCount ?? 0,
  });
  const activeCta: AudienceSourceApplyCta =
    sourceTab === "contacts" ? contactCta : sourceTab === "groups" ? groupCta : csvCta;
  const reviewDisabled =
    anySourceDirty || previewBlocked || saving || !hasSources || !canMutate;

  const selectTab = (tab: CampaignManageSourceTab) => {
    switch (tab) {
      case "contacts":
        onViewChange("contacts");
        return;
      case "groups":
        onViewChange("groups");
        return;
      case "csv":
        onViewChange("csv");
        return;
      default: {
        const _exhaustive: never = tab;
        return _exhaustive;
      }
    }
  };

  const openReview = () => {
    if (anySourceDirty) return;
    onViewChange("review");
    void onPreview();
  };

  const applyActiveSource = async () => {
    if (sourceTab === "contacts") {
      await onSetContactIds(contactDraftIds);
      return;
    }
    if (sourceTab === "groups") {
      await onSetGroupIds(groupDraftIds);
      return;
    }
    if (upload?.uploadId) {
      await onApplyCsv(upload.uploadId);
      setAppliedCsvUploadId(upload.uploadId);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent size={isReview ? "md" : "lg"}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isReview ? "review" : sourceTab}
            className="flex min-h-0 flex-1 flex-col"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: paneDuration, ease: "easeOut" }}
          >
            {isReview ? (
              <>
                <div className={contactsModalHeaderClass}>
                  <div className="flex items-start gap-2.5">
                    <span
                      className="inline-grid size-8 shrink-0 place-items-center rounded-md border border-line/70 bg-surface-muted text-muted"
                      aria-hidden="true"
                    >
                      <Users size={16} />
                    </span>
                    <div className="grid min-w-0 gap-1">
                      <AlertDialogTitle>{t("campaigns.preview.title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("campaigns.preview.subtitle")}</AlertDialogDescription>
                    </div>
                  </div>
                </div>
                <div className={contactsModalBodyClass}>
                  {error ? (
                    <p className="mb-3 rounded-md border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error" role="alert">
                      {mapCampaignError(t, error, t("campaigns.errors.confirmFailed"))}
                    </p>
                  ) : null}
                  {previewForCurrent ? (
                    <CampaignAudiencePreview
                      preview={previewForCurrent}
                      emptyReady={readyCount === 0}
                      contactCount={definition.contactIds.length}
                      groups={selectedGroups}
                      csvFileName={acknowledgedCsvFileName}
                    />
                  ) : (
                    <p className="text-[0.8125rem] text-muted">{t("campaigns.loading")}</p>
                  )}
                </div>
                <div className={contactsModalFooterClass}>
                  <Button type="button" variant="outline" onClick={() => onViewChange("contacts")}>
                    {t("campaigns.manage.back")}
                  </Button>
                  {canMutate ? (
                    <Button
                      type="button"
                      disabled={readyCount === 0 || saving}
                      data-testid="campaign-audience-review-confirm"
                      onClick={async () => {
                        await onConfirm();
                        onOpenChange(false);
                      }}
                    >
                      {saving ? t("campaigns.preview.confirming") : t("campaigns.preview.confirm")}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <div className={contactsModalHeaderClass}>
                  <div className="flex items-start gap-2.5">
                    <span
                      className="inline-grid size-8 shrink-0 place-items-center rounded-md border border-line/70 bg-surface-muted text-muted"
                      aria-hidden="true"
                    >
                      <Users size={16} />
                    </span>
                    <div className="grid min-w-0 gap-1">
                      <AlertDialogTitle>{t("campaigns.manage.title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("campaigns.manage.subtitle")}</AlertDialogDescription>
                    </div>
                  </div>
                </div>
                <div className="shrink-0" data-testid="campaign-manage-source-choices">
                  <CampaignAudienceSourceTabs
                    active={sourceTab}
                    disabled={!canMutate}
                    counts={{
                      contacts: contactDraftIds.length,
                      groups: groupDraftIds.length,
                      csv: upload ? 1 : 0,
                    }}
                    onSelect={selectTab}
                  />
                </div>
                <div className="shrink-0">
                  <CampaignAudienceCurrentSources
                    contactCount={definition.contactIds.length}
                    groups={selectedGroups}
                    csvFileName={acknowledgedCsvFileName}
                    canMutate={canMutate}
                    saving={saving}
                    onSelectTab={selectTab}
                    onReplaceCsv={() => onViewChange("csv")}
                    onRemoveCsv={async () => {
                      await onRemoveUpload();
                      setAppliedCsvUploadId(null);
                    }}
                  />
                </div>
                {error ? (
                  <p className="mx-6 mb-2 rounded-md border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error" role="alert">
                    {mapCampaignError(t, error, t("campaigns.errors.requestFailed"))}
                  </p>
                ) : null}
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {sourceTab === "contacts" ? (
                  <CampaignContactPickerPanel
                    organizationId={organizationId}
                    selectedIds={contactDraftIds}
                    busy={saving}
                    embedded
                    onSelectionChange={setContactDraftIds}
                    onCancel={() => onOpenChange(false)}
                    onConfirm={onSetContactIds}
                  />
                ) : null}
                {sourceTab === "groups" ? (
                  <CampaignGroupPickerPanel
                    groups={groups}
                    selectedIds={groupDraftIds}
                    busy={saving}
                    embedded
                    onSelectionChange={setGroupDraftIds}
                    onCancel={() => onOpenChange(false)}
                    onConfirm={onSetGroupIds}
                  />
                ) : null}
                {sourceTab === "csv" ? (
                  <CampaignCsvPanel
                    upload={upload}
                    preview={uploadPreview}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    polling={polling}
                    canMutate={canMutate}
                    busy={saving}
                    onUpload={onUploadCsv}
                    onConfigure={onConfigureUpload}
                    onRemove={async () => {
                      await onRemoveUpload();
                      setAppliedCsvUploadId(null);
                    }}
                  />
                ) : null}
                </div>
                <div className={contactsModalFooterClass}>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    {t("campaigns.actions.cancel")}
                  </Button>
                  {canMutate ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={reviewDisabled}
                      title={anySourceDirty ? t("campaigns.preview.saveSourcesFirst") : undefined}
                      data-testid="campaign-audience-review"
                      onClick={openReview}
                    >
                      {anySourceDirty
                        ? t("campaigns.preview.saveSourcesFirst")
                        : t("campaigns.preview.action")}
                    </Button>
                  ) : null}
                  {canMutate && activeCta.show ? (
                    <Button
                      type="button"
                      disabled={activeCta.disabled || saving}
                      data-testid="campaign-source-apply"
                      onClick={() => void applyActiveSource()}
                    >
                      {t(sourceApplyI18nKey(sourceTab, activeCta.kind), {
                        count: activeCta.count,
                      })}
                    </Button>
                  ) : null}
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </CampaignModalContent>
    </AlertDialog>
  );
}
